import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '../../environments/environment';
import { AuthService } from './auth/auth-service';
import { WsMessage } from '../models/web-socket-message';
import { Token } from '../models/token';

export const WS_RECONNECT_BASE_MS = 3000;
export const WS_RECONNECT_MAX_MS = 300000;
export const WS_RECONNECT_MAX_ATTEMPTS = 10;
// Uvicorn pings every 20 s, so only a proxy idle timeout cuts a socket sooner.
export const WS_STABLE_CONNECTION_MS = 60000;

/** Exponential backoff with equal jitter: a random wait in [step / 2, step]. */
export function wsReconnectDelay(
  attempt: number,
  random: () => number = Math.random
): number {
  const step = Math.min(
    WS_RECONNECT_MAX_MS,
    WS_RECONNECT_BASE_MS * 2 ** (attempt - 1)
  );
  return Math.round(step / 2 + (random() * step) / 2);
}

@Injectable()
export class WebsocketService implements OnDestroy {
  public connection?: WebSocketSubject<any>;
  public messageReceived$ = new EventEmitter<WsMessage>();
  private failedAttempts = 0;
  private openedAt?: number;
  private reconnectTimer?: ReturnType<typeof setTimeout>;

  constructor(private readonly authService: AuthService) {}

  public connect(): void {
    if (this.connection) {
      return;
    }
    this.openedAt = undefined;
    this.connection = new WebSocketSubject({
      url: environment.WS_URL + '/backend/ws/user-updates-stream/',
      openObserver: {
        next: async () => {
          this.openedAt = Date.now();
          let jwt = localStorage.getItem(Token.tokenNameInStorage);
          if (jwt) {
            jwt = jwt.replace('Bearer ', '');
            await this.send({
              type: 'authenticate',
              jwt: jwt
            });
          }
        }
      }
    });
    this.connection.subscribe({
      next: (message: WsMessage) => {
        this.messageReceived(message);
      },
      error: () => this.scheduleReconnect()
    });
  }

  public async send(data: any) {
    const tokenJWT = localStorage.getItem(Token.tokenNameInStorage);
    if (this.connection) {
      this.connection.next({
        token: tokenJWT,
        ...data
      });
    }
  }

  public messageReceived(message: WsMessage) {
    this.messageReceived$.emit(message);
  }

  public closeConnection() {
    clearTimeout(this.reconnectTimer);
    if (this.connection) {
      this.connection.complete();
      this.connection = undefined;
    }
  }

  public ngOnDestroy() {
    this.closeConnection();
  }

  private scheduleReconnect() {
    this.connection = undefined;
    if (
      this.openedAt !== undefined &&
      Date.now() - this.openedAt >= WS_STABLE_CONNECTION_MS
    ) {
      this.failedAttempts = 0;
    }
    this.failedAttempts++;
    if (this.failedAttempts > WS_RECONNECT_MAX_ATTEMPTS) {
      console.warn(
        'WebSocket disconnected, no retries left: live updates are off until the page is reloaded'
      );
      return;
    }
    const delay = wsReconnectDelay(this.failedAttempts);
    console.warn(
      `WebSocket disconnected, retry ${this.failedAttempts}/${WS_RECONNECT_MAX_ATTEMPTS} in ${Math.round(delay / 1000)} s`
    );
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }
}
